// Formatting helpers for chat timestamps.

const SHORT_DAY_FMT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const FULL_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const ONE_MIN = 60_000;
const ONE_HOUR = 60 * ONE_MIN;
const ONE_DAY = 24 * ONE_HOUR;

export function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < ONE_MIN) return 'just now';
  if (ms < ONE_HOUR) return `${Math.floor(ms / ONE_MIN)}m`;
  if (ms < ONE_DAY) return `${Math.floor(ms / ONE_HOUR)}h`;
  if (ms < ONE_DAY * 7) return SHORT_DAY_FMT.format(new Date(iso));
  return FULL_DATE_FMT.format(new Date(iso));
}

export function timeOfDay(iso: string): string {
  return TIME_FMT.format(new Date(iso));
}

// Verbose relative-time variant for "last active" lines and session lists.
// Distinct from `relativeTime` so we can be more conversational here without
// breaking the compact suffixes used by chat list rows.
export function lastActiveLabel(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'Active now';
  if (ms < ONE_MIN) return 'Active now';
  if (ms < ONE_HOUR) {
    const m = Math.max(1, Math.floor(ms / ONE_MIN));
    return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (ms < ONE_DAY) {
    const h = Math.floor(ms / ONE_HOUR);
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  }
  if (ms < ONE_DAY * 7) {
    const d = Math.floor(ms / ONE_DAY);
    return `${d} ${d === 1 ? 'day' : 'days'} ago`;
  }
  return FULL_DATE_FMT.format(new Date(iso));
}

export function dateHeading(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameYMD = date.toDateString() === today.toDateString();
  if (sameYMD) return 'Today';

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return FULL_DATE_FMT.format(date);
}
